import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { api } from "@/lib/api";
import { applyChartClasses } from "@/lib/chart-prefs";
import {
  browserPreferencesToAdopt,
  dropLegacyMaxwellOpen,
  legacyPreferenceKeys,
  readBrowserPreferences,
  resolvePreferences,
} from "@/lib/preferences";
import { applyThemeClasses } from "@/lib/theme";
import type { Session } from "@/types/session";
import type { UserPreferences } from "@/types/users";

/**
 * Keeps `<html>` at the session's look: theme mode and palette, chart weight
 * and colours. Called once, by `AppShell`, before its session gate —
 * `session` is `undefined` until it resolves, and the pre-paint script's copy
 * of the last look stands in until then.
 */
export function useApplyPreferences(session: Session | undefined) {
  const effective = session
    ? resolvePreferences(session, readBrowserPreferences(session.user_id))
    : null;
  const mode = effective?.theme_mode;
  const palette = effective?.theme_palette;
  const weight = effective?.chart_line_weight;
  const colors = effective?.chart_colors;

  useEffect(() => {
    if (mode && palette) applyThemeClasses(mode, palette);
  }, [mode, palette]);

  useEffect(() => {
    if (weight && colors) applyChartClasses({ chart_line_weight: weight, chart_colors: colors });
  }, [weight, colors]);
}

function adoptedMarker(userId: string): string {
  return `prefs-adopted:${userId}`;
}

/**
 * Uploads, once per user per browser, the choices this browser made before
 * preferences moved to the server — so nobody's look or units reset on the
 * deploy that moved them. Only fills preferences the server has never been
 * told (`browserPreferencesToAdopt`), then drops the old keys nothing reads.
 *
 * Silent: it is housekeeping, not a user action, so a failure just tries
 * again on the next load. Skipped for a read-only session (a super admin
 * viewing as someone), which refuses writes. Called once, by `AppShell`.
 */
export function useAdoptBrowserPreferences(session: Session | undefined) {
  const qc = useQueryClient();
  const attempted = useRef<string | null>(null);
  const userId = session?.user_id;
  const stored = session?.preferences;
  const readOnly = session?.impersonating ?? true;

  useEffect(() => {
    if (!userId || !stored || readOnly || attempted.current === userId) return;
    attempted.current = userId;
    try {
      if (localStorage.getItem(adoptedMarker(userId))) return;
    } catch {
      return;
    }

    const finish = () => {
      try {
        localStorage.setItem(adoptedMarker(userId), "1");
        for (const key of legacyPreferenceKeys(userId)) localStorage.removeItem(key);
        dropLegacyMaxwellOpen();
      } catch {
        // ignore: private-mode storage failures
      }
    };
    const patch = browserPreferencesToAdopt(stored, readBrowserPreferences(userId));
    if (Object.keys(patch).length === 0) {
      finish();
      return;
    }
    api
      .patch<UserPreferences>("/api/me/preferences", patch)
      .then((preferences) => {
        qc.setQueryData<Session>(["session"], (s) => (s ? { ...s, preferences } : s));
        finish();
      })
      .catch(() => {
        // Try again on the next load.
      });
  }, [qc, userId, stored, readOnly]);
}
